import React, { useState } from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { CallbackModal } from '../components/CallbackModal';
import { MapPin, Star, Scan, Languages, Calendar, MessageCircle, CheckCircle2, Plus, Minus, Sparkles, Smile, Palette } from 'lucide-react';
import { Link } from 'react-router-dom';
const BOOKING_URL = 'https://denticoclinic.patientportal.onl/bookings';
const WHATSAPP_URL = 'https://wa.me/447345197820';
const TELEGRAM_URL = 'https://t.me/dentico_clinic';
function TelegramIcon({
  className
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 24 24" fill="currentColor" className={className} data-id="element-3754">
      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" data-id="element-3755" />
    </svg>;
}
function AccordionItem({
  question,
  answer,
  isOpen,
  onToggle
}: {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return <div className="border-b border-gray-100 last:border-b-0" data-id="element-3756">
      <button onClick={onToggle} className="w-full flex items-center justify-between py-4 text-left focus:outline-none group" data-id="element-3757">
        <span className="text-lg font-medium text-gray-900 group-hover:text-teal-600 transition-colors" data-id="element-3758">
          {question}
        </span>
        <span className={`flex-shrink-0 ml-4 w-6 h-6 rounded-full flex items-center justify-center transition-colors ${isOpen ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-400'}`} data-id="element-3759">
          {isOpen ? <Minus className="w-4 h-4" data-id="element-3760" /> : <Plus className="w-4 h-4" data-id="element-3761" />}
        </span>
      </button>
      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-96 opacity-100 pb-4' : 'max-h-0 opacity-0'}`} data-id="element-3762">
        <p className="text-gray-600 leading-relaxed" data-id="element-3763">{answer}</p>
      </div>
    </div>;
}
export function VeneersLondonRuPage() {
  const [isCallbackOpen, setIsCallbackOpen] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };
  const faqs = [{
    q: 'Сколько стоят виниры в Лондоне?',
    a: 'Цена зависит от материала (керамика или композит), количества зубов и сложности работы. После консультации мы предлагаем детальный план лечения и смету.'
  }, {
    q: 'Нужно ли обтачивать зубы под виниры?',
    a: 'В большинстве случаев требуется минимальная подготовка эмали (0.3-0.5 мм), но объем зависит от клинической ситуации. Мы всегда выбираем максимально щадящий подход (minimally invasive).'
  }, {
    q: 'Сколько служат виниры?',
    a: 'Керамические виниры могут служить 10-15 лет и более при правильном уходе. Срок службы зависит от материала, прикуса и гигиены. На консультации мы объясняем, как продлить долговечность.'
  }, {
    q: 'Можно ли сделать естественный результат?',
    a: 'Да, это наша специализация. Мы подбираем форму, текстуру и цвет индивидуально, чтобы улыбка выглядела гармонично и натурально, а не искусственно.'
  }, {
    q: 'Какая разница между фарфоровыми и композитными винирами?',
    a: 'Фарфоровые (керамические) виниры прочнее, долговечнее и устойчивее к окрашиванию. Композитные виниры (bonding) доступнее по цене и часто устанавливаются за один визит, но требуют более частой полировки.'
  }, {
    q: 'Сколько визитов нужно для виниров?',
    a: 'Обычно 2-3 визита для керамических виниров: консультация, подготовка/оттиски, установка. Композитные виниры часто можно сделать за один визит.'
  }, {
    q: 'Можно ли получить консультацию на русском?',
    a: 'Да, в Dentico Clinic возможно общение на русском языке во время консультации и лечения.'
  }, {
    q: 'Где находится Dentico Clinic?',
    a: 'Angel / Islington, 360–364 City Road, London EC1V 2PY. 5 минут пешком от станции Angel.'
  }];
  // Schemas
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.a
      }
    }))
  };
  const procedureSchema = {
    '@context': 'https://schema.org',
    '@type': 'MedicalProcedure',
    name: 'Dental Veneers',
    description: 'Cosmetic dental procedure to improve the appearance of teeth using veneers.',
    procedureType: 'http://schema.org/NoninvasiveProcedure',
    provider: {
      '@type': 'Dentist',
      name: 'Dentico Clinic',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '360-364 City Road',
        addressLocality: 'London',
        postalCode: 'EC1V 2PY',
        addressCountry: 'GB'
      }
    }
  };
  return <Layout data-id="element-3764">
      <CallbackModal isOpen={isCallbackOpen} onClose={() => setIsCallbackOpen(false)} data-id="element-3765" />

      {/* Structured Data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(faqSchema)
    }} data-id="element-3766" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(procedureSchema)
    }} data-id="element-3767" />

      {/* 1. HERO SECTION */}
      <section className="relative bg-white overflow-hidden" data-id="element-3768">
        <div className="absolute inset-0 bg-teal-50/50 -skew-y-3 origin-top-left transform translate-y-20" data-id="element-3769" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-16 lg:pt-20 lg:pb-24 relative" data-id="element-3770">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-3771">
            <div className="space-y-6" data-id="element-3772">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-100 text-teal-800 text-sm font-medium" data-id="element-3773">
                <span className="relative flex h-2 w-2" data-id="element-3774">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-75" data-id="element-3775"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-teal-500" data-id="element-3776"></span>
                </span>
                Эстетическая стоматология
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight tracking-tight" data-id="element-3777">
                Виниры в Лондоне –{' '}
                <span className="text-teal-600" data-id="element-3778">Dentico Clinic</span>
                <br data-id="element-3779" />
                <span className="text-2xl sm:text-3xl font-medium text-gray-500 mt-2 block" data-id="element-3780">
                  (Angel, Islington)
                </span>
              </h1>

              <p className="text-lg text-gray-600 max-w-lg leading-relaxed" data-id="element-3781">
                Создайте идеальную улыбку с помощью керамических виниров или
                композитного бондинга. Естественный результат, минимальное
                вмешательство, консультация на русском.
              </p>

              {/* Trust Badges */}
              <div className="flex flex-wrap gap-3 text-sm font-medium text-gray-700" data-id="element-3782">
                <div className="flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-lg border border-gray-200 shadow-sm" data-id="element-3783">
                  <Star className="w-4 h-4 text-yellow-400 fill-current" data-id="element-3784" />
                  Smile Makeover
                </div>
                <div className="flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-lg border border-gray-200 shadow-sm" data-id="element-3785">
                  <MapPin className="w-4 h-4 text-teal-600" data-id="element-3786" />
                  Angel, EC1V
                </div>
                <div className="flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-lg border border-gray-200 shadow-sm" data-id="element-3787">
                  <Languages className="w-4 h-4 text-teal-600" data-id="element-3788" />
                  Русский язык
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-4" data-id="element-3789">
                <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-3790">
                  <Button size="lg" className="w-full gap-2 shadow-lg shadow-teal-600/20" data-id="element-3791">
                    <Calendar className="w-5 h-5" data-id="element-3792" />
                    Записаться на консультацию
                  </Button>
                </a>
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-3793">
                  <Button variant="outline" size="lg" className="w-full gap-2 border-teal-200 text-teal-700 hover:bg-teal-50" data-id="element-3794">
                    <MessageCircle className="w-5 h-5 text-green-600" data-id="element-3795" />
                    WhatsApp
                  </Button>
                </a>
              </div>
            </div>

            <div className="relative" data-id="element-3796">
              <div className="absolute inset-0 bg-teal-200 rounded-3xl transform rotate-3 blur-sm opacity-30" data-id="element-3797"></div>
              <img src="https://images.unsplash.com/photo-1579684385127-1ef15d508118?auto=format&fit=crop&w=1200&q=80" alt="Виниры и эстетическая стоматология - Dentico Clinic" className="relative rounded-3xl shadow-2xl object-cover h-[300px] sm:h-[400px] lg:h-[500px] w-full" data-id="element-3798" />
            </div>
          </div>
        </div>
      </section>

      {/* 2. WHAT ARE VENEERS */}
      <section className="py-16 bg-white" data-id="element-3799">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3800">
          <div className="text-center mb-10" data-id="element-3801">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-3802">
              Что такое виниры?
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed" data-id="element-3803">
              Виниры — это ультратонкие керамические накладки, которые
              фиксируются на переднюю поверхность зубов для улучшения их формы,
              цвета и положения. Это идеальное решение для коррекции сколов,
              промежутков между зубами (диастем), изменения цвета эмали, который
              не поддается отбеливанию, или выравнивания незначительных
              неровностей.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed mt-4" data-id="element-3804">
              Современные виниры выглядят абсолютно естественно благодаря
              способности пропускать свет так же, как натуральная эмаль. В
              Dentico Clinic мы используем цифровые технологии для моделирования
              вашей будущей улыбки еще до начала лечения.
            </p>
          </div>
        </div>
      </section>

      {/* 3. PROCESS */}
      <section className="py-16 bg-gray-50" data-id="element-3805">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3806">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center" data-id="element-3807">
            Путь к новой улыбке
          </h2>
          <div className="grid md:grid-cols-4 gap-8" data-id="element-3808">
            {[{
            step: '01',
            title: 'Консультация',
            desc: 'Обсуждение пожеланий, фотопротокол и цифровое моделирование улыбки.'
          }, {
            step: '02',
            title: 'Планирование',
            desc: 'Примерка макета (mock-up), чтобы вы увидели результат до начала работ.'
          }, {
            step: '03',
            title: 'Подготовка',
            desc: 'Минимальная обработка зубов и снятие точных оттисков или сканирование.'
          }, {
            step: '04',
            title: 'Фиксация',
            desc: 'Установка готовых керамических виниров и финальная эстетика.'
          }].map((item, idx) => <Card key={idx} className="p-6 relative overflow-hidden" data-id="element-3809">
                <div className="text-6xl font-bold text-teal-50 absolute -top-4 -right-4 select-none" data-id="element-3810">
                  {item.step}
                </div>
                <div className="relative z-10" data-id="element-3811">
                  <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-3812">
                    {item.title}
                  </h3>
                  <p className="text-gray-600" data-id="element-3813">{item.desc}</p>
                </div>
              </Card>)}
          </div>
        </div>
      </section>

      {/* 4. TYPES */}
      <section className="py-16 bg-white" data-id="element-3814">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3815">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-3816">
            <div data-id="element-3817">
              <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-3818">
                Виды эстетической реставрации
              </h2>
              <div className="space-y-6" data-id="element-3819">
                <div className="flex gap-4" data-id="element-3820">
                  <div className="p-3 bg-teal-50 rounded-lg text-teal-600 h-fit" data-id="element-3821">
                    <Sparkles className="w-6 h-6" data-id="element-3822" />
                  </div>
                  <div data-id="element-3823">
                    <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-3824">
                      Керамические виниры
                    </h3>
                    <p className="text-gray-600" data-id="element-3825">
                      Изготавливаются в лаборатории из высокопрочной керамики.
                      Максимально эстетичные, долговечные и устойчивые к
                      изменению цвета.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4" data-id="element-3826">
                  <div className="p-3 bg-teal-50 rounded-lg text-teal-600 h-fit" data-id="element-3827">
                    <Palette className="w-6 h-6" data-id="element-3828" />
                  </div>
                  <div data-id="element-3829">
                    <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-3830">
                      Композитный бондинг
                    </h3>
                    <p className="text-gray-600" data-id="element-3831">
                      Художественная реставрация композитным материалом прямо в
                      кресле. Быстрее и доступнее, но требует периодической
                      полировки.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4" data-id="element-3832">
                  <div className="p-3 bg-teal-50 rounded-lg text-teal-600 h-fit" data-id="element-3833">
                    <Smile className="w-6 h-6" data-id="element-3834" />
                  </div>
                  <div data-id="element-3835">
                    <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-3836">
                      Smile Makeover
                    </h3>
                    <p className="text-gray-600" data-id="element-3837">
                      Комплексное преображение улыбки, которое может сочетать
                      виниры, коронки и отбеливание для идеального результата.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative h-[400px] rounded-2xl overflow-hidden shadow-xl" data-id="element-3838">
              <img src="https://images.unsplash.com/photo-1606811841689-23dfddce3e95?auto=format&fit=crop&w=1200&q=80" alt="Типы виниров" className="absolute inset-0 w-full h-full object-cover" data-id="element-3839" />
            </div>
          </div>
        </div>
      </section>

      {/* 5. BENEFITS */}
      <section className="py-16 bg-teal-50" data-id="element-3840">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3841">
          <div className="text-center mb-12" data-id="element-3842">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-3843">
              Почему стоит выбрать Dentico Clinic
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-3844">
            {[{
            title: 'Естественный вид',
            desc: 'Мы создаем улыбки, которые выглядят натурально, а не искусственно.'
          }, {
            title: 'Минимальное вмешательство',
            desc: 'Сохраняем максимум здоровых тканей зуба благодаря современным техникам.'
          }, {
            title: 'Русский язык',
            desc: 'Легкое общение и полное понимание ваших пожеланий.'
          }, {
            title: 'Удобная локация',
            desc: 'Клиника в центре Лондона (Angel), легко добраться из любого района.'
          }].map((item, idx) => <Card key={idx} className="p-6 bg-white text-center" data-id="element-3845">
                <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center text-teal-600 mb-4 mx-auto" data-id="element-3846">
                  <CheckCircle2 className="w-6 h-6" data-id="element-3847" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-3848">
                  {item.title}
                </h3>
                <p className="text-gray-600" data-id="element-3849">{item.desc}</p>
              </Card>)}
          </div>
        </div>
      </section>

      {/* 6. FAQ */}
      <section className="py-16 bg-white" data-id="element-3850">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3851">
          <div className="text-center mb-10" data-id="element-3852">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-3853">
              Частые вопросы о винирах
            </h2>
          </div>
          <div className="space-y-2" data-id="element-3854">
            {faqs.map((faq, idx) => <AccordionItem key={idx} question={faq.q} answer={faq.a} isOpen={openFaqIndex === idx} onToggle={() => toggleFaq(idx)} data-id="element-3855" />)}
          </div>
        </div>
      </section>

      {/* 7. INTERNAL LINKS */}
      <section className="py-12 bg-gray-50 border-t border-gray-100" data-id="element-3856">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3857">
          <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-3858">
            Смотрите также
          </h2>
          <div className="flex flex-wrap gap-4" data-id="element-3859">
            <Link to="/ru/russkij-stomatolog-london" data-id="element-3860">
              <Button variant="outline" size="sm" data-id="element-3861">
                Русскоязычный стоматолог
              </Button>
            </Link>
            <Link to="/ru/services/smile-makeover" data-id="element-3862">
              <Button variant="outline" size="sm" data-id="element-3863">
                Smile Makeover
              </Button>
            </Link>
            <Link to="/ru/services/whitening" data-id="element-3864">
              <Button variant="outline" size="sm" data-id="element-3865">
                Отбеливание
              </Button>
            </Link>
            <Link to="/ru/prices" data-id="element-3866">
              <Button variant="outline" size="sm" data-id="element-3867">
                Цены
              </Button>
            </Link>
            <Link to="/ru/contact" data-id="element-3868">
              <Button variant="outline" size="sm" data-id="element-3869">
                Контакты
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* 8. CTA */}
      <section className="py-16 bg-teal-900" data-id="element-3870">
        <div className="max-w-4xl mx-auto px-4 text-center" data-id="element-3871">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4" data-id="element-3872">
            Мечтаете об идеальной улыбке?
          </h2>
          <p className="text-xl text-teal-100 mb-8 max-w-2xl mx-auto" data-id="element-3873">
            Запишитесь на консультацию и узнайте, как виниры могут изменить вашу
            жизнь.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-3874">
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-3875">
              <Button size="lg" className="w-full gap-2 bg-white text-teal-900 hover:bg-teal-50" data-id="element-3876">
                <Calendar className="w-5 h-5" data-id="element-3877" />
                Записаться онлайн
              </Button>
            </a>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-3878">
              <Button variant="outline" size="lg" className="w-full gap-2 border-teal-400 text-teal-100 hover:bg-teal-800 hover:text-white" data-id="element-3879">
                <MessageCircle className="w-5 h-5" data-id="element-3880" />
                WhatsApp
              </Button>
            </a>
          </div>
        </div>
      </section>
    </Layout>;
}